// =============================================================================
// PPIChaoVersoes — o CONSTRUTOR do formulário da avaliação de chão
// =============================================================================
// As perguntas da avaliação de chão ainda não existem (a Patrícia não mandou).
// Em vez de esperar — ou de chutar e depois ter de mexer no código —, o Treinamento
// monta o formulário AQUI: áreas → perguntas (com peso). Publica, e a versão
// CONGELA assim que a primeira avaliação for gravada (mexer depois faria as
// avaliações antigas mentirem: o candidato foi avaliado por OUTRO formulário).
// Quem quiser mudar, DUPLICA. Mesmo padrão do BSV e do Checklist.

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ArrowLeft, Save, Send, Copy, Archive, Lock } from 'lucide-react';
import { CORES, BadgeStatus, Botao, estilos } from './ppiUI';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

const API = '/api/treinamento/ppi/chao';

const TOM = { Rascunho: 'rascunho', Publicado: 'ativo', Arquivado: 'neutro' };

function PPIChaoVersoes() {
    const [cargos, setCargos] = useState([]);
    const [idCargo, setIdCargo] = useState('');
    const [versoes, setVersoes] = useState([]);
    const [editando, setEditando] = useState(null);   // id da versão aberta
    const [erro, setErro] = useState('');
    const [msg, setMsg] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch('/api/treinamento/ppi/prova/cargos', { headers: authHeaders() });
                if (r.ok) {
                    const cs = await r.json();
                    setCargos(cs);
                    if (cs.length === 1) setIdCargo(String(cs[0].ID));
                }
            } catch (_) { /* silencioso */ }
        })();
    }, []);

    const carregar = useCallback(async () => {
        try {
            const q = idCargo ? `?cargo=${idCargo}` : '';
            const r = await fetch(`${API}/versoes${q}`, { headers: authHeaders() });
            if (r.ok) setVersoes(await r.json());
        } catch (e) { setErro('Erro ao carregar as versões.'); }
    }, [idCargo]);

    useEffect(() => { carregar(); }, [carregar]);

    const criar = async () => {
        if (!idCargo) { setErro('Escolha a trilha antes de criar o formulário.'); return; }
        const nome = window.prompt('Nome da versão (ex.: "LIVE — Treinador v1"):');
        if (!nome?.trim()) return;
        try {
            const r = await fetch(`${API}/versoes`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ idCargo: Number(idCargo), nomeVersao: nome.trim() }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setEditando(d.id);
        } catch (e) { setErro(e.message); }
    };

    const acao = async (id, rota, confirmacao) => {
        if (confirmacao && !window.confirm(confirmacao)) return;
        setErro(''); setMsg('');
        try {
            const r = await fetch(`${API}/versoes/${id}/${rota}`, { method: 'POST', headers: authHeaders() });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setMsg(d.message);
            carregar();
        } catch (e) { setErro(e.message); }
    };

    const apagar = async (id) => {
        if (!window.confirm('Apagar esta versão?')) return;
        try {
            const r = await fetch(`${API}/versoes/${id}`, { method: 'DELETE', headers: authHeaders() });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            carregar();
        } catch (e) { setErro(e.message); }
    };

    if (editando) {
        return <EditorVersao id={editando} onVoltar={() => { setEditando(null); carregar(); }} />;
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <div style={{ minWidth: '280px' }}>
                    <label style={estilos.rotuloFiltro}>Trilha de promoção</label>
                    <select style={estilos.input} value={idCargo} onChange={(e) => setIdCargo(e.target.value)}>
                        <option value="">Todas</option>
                        {cargos.map((c) => <option key={c.ID} value={c.ID}>{c.NOME}</option>)}
                    </select>
                </div>
                <Botao icone={Plus} onClick={criar}>Novo formulário</Botao>
            </div>

            <p style={{ fontSize: '13px', color: CORES.textoFraco, margin: '0 0 16px 0', maxWidth: '760px' }}>
                Monte aqui o formulário que o Treinamento vai preencher na loja, 30 dias depois da prova.
                Publicada e <strong>usada em uma avaliação, a versão congela</strong> — editar depois faria as
                avaliações antigas mentirem (o candidato foi avaliado por outro formulário). Para mudar, <strong>duplique</strong>.
            </p>

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
            {msg && <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '10px 12px', borderRadius: '8px', marginBottom: '14px', fontSize: '14px' }}>{msg}</div>}

            {!versoes.length && <p style={{ color: CORES.textoFraco }}>Nenhum formulário criado ainda.</p>}

            <div style={estilos.tabelaWrap}>
                <table style={estilos.tabela}>
                    <thead><tr>
                        <th style={estilos.th}>Versão</th>
                        <th style={estilos.th}>Trilha</th>
                        <th style={estilos.th}>Status</th>
                        <th style={{ ...estilos.th, textAlign: 'center' }}>Peso total</th>
                        <th style={{ ...estilos.th, textAlign: 'center' }}>Avaliações</th>
                        <th style={{ ...estilos.th, textAlign: 'right' }}>Ações</th>
                    </tr></thead>
                    <tbody>
                        {versoes.map((v) => {
                            const congelada = v.STATUS !== 'Rascunho' && Number(v.USOS) > 0;
                            return (
                                <tr key={v.ID}>
                                    <td style={estilos.td}><strong>{v.NOME_VERSAO}</strong></td>
                                    <td style={estilos.td}>{v.CARGO_PPI}</td>
                                    <td style={estilos.td}><BadgeStatus tom={TOM[v.STATUS]}>{v.STATUS}</BadgeStatus></td>
                                    <td style={{ ...estilos.td, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{v.PESO_MAXIMO_TOTAL}</td>
                                    <td style={{ ...estilos.td, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                        {v.USOS}
                                        {congelada && <Lock size={12} style={{ marginLeft: '5px', verticalAlign: 'middle', color: CORES.textoFraco }} />}
                                    </td>
                                    <td style={{ ...estilos.td, textAlign: 'right' }}>
                                        <div style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            <Botao variant="secundario" onClick={() => setEditando(v.ID)}>
                                                {congelada ? 'Ver' : 'Editar'}
                                            </Botao>
                                            {v.STATUS === 'Rascunho' && (
                                                <Botao icone={Send} onClick={() => acao(v.ID, 'publicar')}>Publicar</Botao>
                                            )}
                                            <Botao variant="secundario" icone={Copy} onClick={() => acao(v.ID, 'duplicar')}>Duplicar</Botao>
                                            {v.STATUS === 'Publicado' && (
                                                <Botao variant="secundario" icone={Archive}
                                                    onClick={() => acao(v.ID, 'arquivar', 'Arquivar esta versão? Ela deixa de ser usada em novas avaliações.')}>
                                                    Arquivar
                                                </Botao>
                                            )}
                                            {Number(v.USOS) === 0 && (
                                                <Botao variant="perigo" icone={Trash2} onClick={() => apagar(v.ID)}>Apagar</Botao>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Editor: áreas → perguntas (com peso) ────────────────────────────────────
function EditorVersao({ id, onVoltar }) {
    const [dados, setDados] = useState(null);
    const [areas, setAreas] = useState([]);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    const [msg, setMsg] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/versoes/${id}`, { headers: authHeaders() });
                const d = await r.json();
                if (!r.ok) throw new Error(d.message);
                setDados(d);
                setAreas(d.areas.map((a) => ({
                    nome: a.NOME,
                    perguntas: a.perguntas.map((p) => ({ texto: p.TEXTO, peso: p.PESO })),
                })));
            } catch (e) { setErro(e.message); }
        })();
    }, [id]);

    const podeEditar = dados?.podeEditar;

    const addArea = () => setAreas((as) => [...as, { nome: '', perguntas: [{ texto: '', peso: 1 }] }]);
    const delArea = (i) => setAreas((as) => as.filter((_, k) => k !== i));
    const setArea = (i, campo, v) => setAreas((as) => as.map((a, k) => k === i ? { ...a, [campo]: v } : a));
    const addPergunta = (i) => setAreas((as) => as.map((a, k) => k === i
        ? { ...a, perguntas: [...a.perguntas, { texto: '', peso: 1 }] } : a));
    const delPergunta = (i, j) => setAreas((as) => as.map((a, k) => k === i
        ? { ...a, perguntas: a.perguntas.filter((_, q) => q !== j) } : a));
    const setPergunta = (i, j, campo, v) => setAreas((as) => as.map((a, k) => k === i
        ? { ...a, perguntas: a.perguntas.map((p, q) => q === j ? { ...p, [campo]: v } : p) } : a));

    const salvar = async () => {
        setSalvando(true); setErro(''); setMsg('');
        try {
            const r = await fetch(`${API}/versoes/${id}`, {
                method: 'PUT', headers: authHeaders(),
                body: JSON.stringify({ areas }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setMsg('Formulário salvo.');
        } catch (e) { setErro(e.message); }
        finally { setSalvando(false); }
    };

    const totalPerguntas = areas.reduce((s, a) => s + a.perguntas.length, 0);
    const pesoTotal = areas.reduce((s, a) => s + a.perguntas.reduce((t, p) => t + (Number(p.peso) || 0), 0), 0);

    if (!dados) return <p style={{ color: CORES.textoFraco }}>Carregando…</p>;

    return (
        <div>
            <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '18px' }}>
                <div>
                    <h3 style={{ ...estilos.tituloSecao, marginBottom: '4px' }}>{dados.versao.NOME_VERSAO}</h3>
                    <p style={{ fontSize: '13px', color: CORES.textoFraco, margin: 0 }}>
                        Trilha: <strong>{dados.versao.CARGO_PPI}</strong> · {totalPerguntas} pergunta(s) · peso total {pesoTotal}
                    </p>
                </div>
                {podeEditar && (
                    <Botao icone={Save} onClick={salvar} disabled={salvando}>
                        {salvando ? 'Salvando…' : 'Salvar formulário'}
                    </Botao>
                )}
            </div>

            {!podeEditar && (
                <div style={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <Lock size={16} color="#c2410c" style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: '#9a3412' }}>
                        <strong>Versão congelada</strong> — já foi usada em {dados.usos} avaliação(ões).
                        Editar as perguntas agora faria essas avaliações mentirem (o candidato foi avaliado por
                        outro formulário). Para mudar, <strong>duplique</strong> esta versão.
                    </span>
                </div>
            )}

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
            {msg && <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '10px 12px', borderRadius: '8px', marginBottom: '14px', fontSize: '14px' }}>{msg}</div>}

            {areas.map((a, i) => (
                <div key={i} style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '16px', marginBottom: '14px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                        <input style={{ ...estilos.input, fontWeight: 700, flex: 1 }} placeholder="Nome da área (ex.: Atendimento ao cliente)"
                            value={a.nome} onChange={(e) => setArea(i, 'nome', e.target.value)} disabled={!podeEditar} />
                        {podeEditar && (
                            <Botao variant="perigo" icone={Trash2} onClick={() => delArea(i)}>Área</Botao>
                        )}
                    </div>

                    {a.perguntas.map((p, j) => (
                        <div key={j} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '12px', color: CORES.textoFraco, width: '22px', textAlign: 'right' }}>{j + 1}.</span>
                            <input style={{ ...estilos.input, flex: 1 }} placeholder="Texto da pergunta"
                                value={p.texto} onChange={(e) => setPergunta(i, j, 'texto', e.target.value)} disabled={!podeEditar} />
                            <input type="number" min="1" style={{ ...estilos.input, width: '84px', fontVariantNumeric: 'tabular-nums' }}
                                title="Peso — quanto esta pergunta vale na nota"
                                value={p.peso} onChange={(e) => setPergunta(i, j, 'peso', e.target.value)} disabled={!podeEditar} />
                            {podeEditar && (
                                <button onClick={() => delPergunta(i, j)} style={btnIconeApagar} title="Remover pergunta">
                                    <Trash2 size={15} />
                                </button>
                            )}
                        </div>
                    ))}

                    {podeEditar && (
                        <button onClick={() => addPergunta(i)} style={btnLink}><Plus size={14} /> pergunta</button>
                    )}
                </div>
            ))}

            {podeEditar && (
                <Botao variant="secundario" icone={Plus} onClick={addArea}>Adicionar área</Botao>
            )}
        </div>
    );
}

const btnVoltar = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: CORES.textoSec, cursor: 'pointer', fontSize: '14px', fontWeight: 600, marginBottom: '16px', padding: 0 };
const btnLink = { display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: CORES.primaria, cursor: 'pointer', fontSize: '13px', fontWeight: 700, padding: 0 };
const btnIconeApagar = { background: 'none', border: 'none', color: '#be123c', cursor: 'pointer', padding: '4px', display: 'flex' };

export default PPIChaoVersoes;
